// @ts-check
// Tabela de filhos de uma relação ONE_TO_MANY com settings.displayAsTable (spec 04): os alunos da
// oportunidade da família, com edição inline, inclusão e remoção. Toda escrita recarrega os filhos
// e o registro pai, porque o motor recalcula etapa efetiva, contagens e valor do card.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { openRecord } from '../side-panel/open-record.js';
import { getIndexView, objectsByNameSingular } from '../../data/metadata-store.js';
import { createLiveRecordList, createRecord, deleteRecordOptimistically, updateRecordOptimistically } from '../../data/record-store.js';
import { findManyRecords } from '../../data/rest.js';
import { FIELD_TYPE, getFieldIcon, getRelationIdFieldName, isFieldReadOnly } from '../../lib/field-metadata.js';
import {
  getChildIdentifyingRelationField,
  getChildLookupColumns,
  getChildStage,
  getChildStageFields,
  getChildTableFields,
  getInverseRelationField,
} from '../../lib/related-records.js';
import { FieldDisplay, RatingStars, ratingToNumber } from '../object-record/field-display.js';
import { FieldInput, INPUT_ONLY_FIELD_TYPES, getInputOnlyPatch, isFieldEditableInline } from '../object-record/field-input.js';
import { Icon } from '../../ui/icon.js';
import { IconButton, LightButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../object-record/field-input.js').FieldPatch} FieldPatch
 */

const LOST_STAGE_VALUE = 'LOST';
const LOST_REASON_FIELD_NAME = 'lostReason';
const OVERRIDE_FIELD_SUFFIX = 'Override';
const CHILD_PAGE_SIZE = 60;
const DEFAULT_COLUMN_WIDTH = 160;
const ACTIONS_COLUMN_WIDTH = 40;

/**
 * Texto por objeto quando o catálogo tem uma versão específica (ex.: "Adicionar aluno").
 * @param {string} key
 * @param {string} objectNameSingular
 * @param {Record<string, unknown>} variables
 */
function translateForObject(key, objectNameSingular, variables) {
  const specificKey = `relatedRecords.${key}ByObject.${objectNameSingular}`;
  return hasTranslation(specificKey) ? t(specificKey, variables) : t(`relatedRecords.${key}`, variables);
}

/**
 * Campo de agrupamento do pai que o filho pode sobrescrever (stage -> stageOverride).
 * @param {ObjectMetadata} parentObject
 * @param {ObjectMetadata} targetObject
 */
function findOverriddenParentFieldName(parentObject, targetObject) {
  for (const field of targetObject.fields) {
    if (field.type !== FIELD_TYPE.SELECT || !field.name.endsWith(OVERRIDE_FIELD_SUFFIX)) continue;
    const parentFieldName = field.name.slice(0, -OVERRIDE_FIELD_SUFFIX.length);
    if (parentObject.fields.some((parentField) => parentField.name === parentFieldName)) return parentFieldName;
  }
  return null;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, record: any, child: any, onOpen: () => void, onRemove: () => void, isReadOnly: boolean, targetObject: ObjectMetadata }} props
 */
function RowActionsMenu({ objectMetadata, child, onOpen, onRemove, isReadOnly, targetObject }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const objectLabel = translateMetadataLabel(targetObject.labelSingular).toLocaleLowerCase('pt-BR');
  return html`<span ref=${anchorRef} class="related-records-row-actions">
    <${IconButton} icon="IconDotsVertical" variant="tertiary" size="small" ariaLabel=${t('relatedRecords.rowActions')} onClick=${(event) => {
      event.stopPropagation();
      setIsOpen(!isOpen);
    }} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${200}>
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconExternalLink" text=${t('relatedRecords.open')} onClick=${() => {
          setIsOpen(false);
          onOpen();
        }} />
        ${isReadOnly
          ? null
          : html`<${MenuItem}
              icon="IconTrash"
              accent="danger"
              text=${translateForObject('remove', targetObject.nameSingular, { object: objectLabel })}
              onClick=${() => {
                setIsOpen(false);
                onRemove();
              }}
            />`}
      <//>
    <//>
  </span>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, field: FieldMetadata, record: any, isReadOnly?: boolean, onChildrenChanged: () => void }} props
 */
export function RelatedRecordsSection({ objectMetadata, field, record, isReadOnly = false, onChildrenChanged }) {
  const targetObject = objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? '');
  const inverseField = getInverseRelationField(field, targetObject);
  const liveList = useMemo(() => createLiveRecordList(targetObject?.nameSingular ?? ''), [targetObject?.nameSingular, record.id]);
  useEffect(() => () => liveList.dispose(), [liveList]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [editingCell, setEditingCell] = useState(/** @type {{ childId: string, fieldId: string } | null} */ (null));
  const [pendingLostChildId, setPendingLostChildId] = useState(/** @type {string | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const children = liveList.records.value;
  const [lookupRecords, setLookupRecords] = useState(/** @type {Map<string, any>} */ (new Map()));
  const identifyingFieldForLookup = getChildIdentifyingRelationField(targetObject, inverseField?.name);
  const identifiedObject = objectsByNameSingular.value.get(identifyingFieldForLookup?.relation?.targetObjectNameSingular ?? '');
  const lookupColumns = targetObject ? getChildLookupColumns(targetObject, identifyingFieldForLookup, identifiedObject) : [];
  const lookupIdFieldName = identifyingFieldForLookup ? getRelationIdFieldName(identifyingFieldForLookup) : '';
  const lookupIdsKey = lookupColumns.length > 0
    ? [...new Set(children.map((child) => child[lookupIdFieldName]).filter(Boolean))].sort().join(',')
    : '';

  useEffect(() => {
    if (!identifiedObject || !lookupIdsKey) return undefined;
    let isCancelled = false;
    findManyRecords(identifiedObject, { filter: { field: 'id', operator: 'in', value: lookupIdsKey.split(',') }, limit: CHILD_PAGE_SIZE, depth: 1 })
      .then((result) => {
        if (!isCancelled) setLookupRecords(new Map(result.records.map((related) => [related.id, related])));
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [identifiedObject?.id, lookupIdsKey, reloadCounter]);

  useEffect(() => {
    if (!targetObject || !inverseField) return undefined;
    let isCancelled = false;
    findManyRecords(targetObject, {
      filter: { field: getRelationIdFieldName(inverseField), operator: 'eq', value: record.id },
      orderBy: [{ field: 'position', direction: 'AscNullsLast' }],
      limit: CHILD_PAGE_SIZE,
      depth: 1,
    })
      .then((result) => {
        if (!isCancelled) liveList.records.value = result.records;
      })
      .catch((error) => {
        if (!isCancelled) showErrorSnackbar(error);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [targetObject?.id, record.id, reloadCounter, liveList]);

  if (!targetObject || !inverseField) return null;

  const indexView = getIndexView(targetObject.id);
  const columnSizes = new Map((indexView?.viewFields ?? []).map((viewField) => [viewField.fieldMetadataId, viewField.size]));
  const columns = getChildTableFields(targetObject, inverseField, indexView);
  const identifyingField = getChildIdentifyingRelationField(targetObject, inverseField.name);
  const parentFieldName = findOverriddenParentFieldName(objectMetadata, targetObject);
  const stageFields = parentFieldName ? getChildStageFields(targetObject, parentFieldName) : { overrideField: undefined, effectiveField: undefined };
  const lostReasonField = targetObject.fields.find((candidate) => candidate.name === LOST_REASON_FIELD_NAME);
  const followsParentLabel = translateForObject('followsParent', objectMetadata.nameSingular, {});
  const objectLabel = translateMetadataLabel(targetObject.labelSingular).toLocaleLowerCase('pt-BR');
  // Larguras proporcionais: a tabela cabe na coluna e os editores flutuantes não são cortados por rolagem.
  const totalWidth = columns.reduce((sum, column) => sum + (columnSizes.get(column.id) ?? DEFAULT_COLUMN_WIDTH), 0) +
    lookupColumns.length * DEFAULT_COLUMN_WIDTH;
  const getColumnWidth = (/** @type {{ id: string }} */ column) => `${(((columnSizes.get(column.id) ?? DEFAULT_COLUMN_WIDTH) / totalWidth) * 100).toFixed(2)}%`;
  // as colunas emprestadas entram logo depois da coluna que identifica o filho (o aluno)
  const identifyingIndex = columns.findIndex((column) => column.id === identifyingField?.id);
  const lookupInsertAt = identifyingIndex >= 0 ? identifyingIndex + 1 : columns.length;
  /** @type {Array<{ kind: 'own', column: FieldMetadata } | { kind: 'lookup', lookup: import('../../lib/related-records.js').LookupColumn }>} */
  const tableColumns = [
    ...columns.slice(0, lookupInsertAt).map((column) => ({ kind: /** @type {const} */ ('own'), column })),
    ...lookupColumns.map((lookup) => ({ kind: /** @type {const} */ ('lookup'), lookup })),
    ...columns.slice(lookupInsertAt).map((column) => ({ kind: /** @type {const} */ ('own'), column })),
  ];

  const handleChildrenChanged = () => {
    setReloadCounter((current) => current + 1);
    onChildrenChanged();
  };

  /**
   * @param {any} child
   */
  const getRowStage = (child) => {
    if (pendingLostChildId === child.id) return LOST_STAGE_VALUE;
    if (!parentFieldName) return child.effectiveStage ?? null;
    return getChildStage(child, record, parentFieldName, stageFields);
  };

  /**
   * Etapa própria LOST exige o motivo: guarda a escolha e abre o motivo antes de salvar os dois juntos.
   * @param {any} child
   * @param {FieldMetadata} column
   * @param {FieldPatch} patch
   */
  const commit = (child, column, patch) => {
    if (!patch) return;
    let { apiPatch, localPatch } = patch;
    const overrideField = stageFields.overrideField;
    if (overrideField && column.id === overrideField.id && apiPatch[overrideField.name] === LOST_STAGE_VALUE && lostReasonField && !child[lostReasonField.name]) {
      setPendingLostChildId(child.id);
      setEditingCell({ childId: child.id, fieldId: lostReasonField.id });
      return;
    }
    if (overrideField && lostReasonField && column.id === lostReasonField.id && pendingLostChildId === child.id) {
      setPendingLostChildId(null);
      if (!apiPatch[lostReasonField.name]) return;
      apiPatch = { ...apiPatch, [overrideField.name]: LOST_STAGE_VALUE };
      localPatch = { ...localPatch, [overrideField.name]: LOST_STAGE_VALUE };
    }
    updateRecordOptimistically(targetObject, child.id, apiPatch, localPatch)
      .then(handleChildrenChanged)
      .catch(() => {});
  };

  const addChild = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const created = await createRecord(targetObject, { [getRelationIdFieldName(inverseField)]: record.id });
      if (created?.id) {
        liveList.records.value = [created, ...liveList.records.value.filter((child) => child.id !== created.id)];
        if (identifyingField) setEditingCell({ childId: created.id, fieldId: identifyingField.id });
      }
      handleChildrenChanged();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * @param {any} child
   */
  const removeChild = async (child) => {
    try {
      await deleteRecordOptimistically(targetObject, child.id);
      showSuccessSnackbar(t('relatedRecords.removed'));
      handleChildrenChanged();
    } catch {
      // deleteRecordOptimistically já mostrou o erro e restaurou a linha.
    }
  };

  /**
   * @param {any} child
   * @param {FieldMetadata} column
   */
  const renderCell = (child, column) => {
    const isLostReasonColumn = lostReasonField?.id === column.id;
    const isLostReasonVisible = !isLostReasonColumn || getRowStage(child) === LOST_STAGE_VALUE;
    const isCellReadOnly = isReadOnly || Boolean(child.deletedAt) || isFieldReadOnly(column) || !isFieldEditableInline(column) || !isLostReasonVisible;
    const isEditing = !isCellReadOnly && editingCell?.childId === child.id && editingCell.fieldId === column.id;
    const isPendingReason = isLostReasonColumn && pendingLostChildId === child.id && !child[column.name];
    const isOverrideColumn = stageFields.overrideField?.id === column.id;
    let display;
    if (!isLostReasonVisible) display = null;
    else if (isPendingReason) display = html`<span class="related-records-required">${t('relatedRecords.required')}</span>`;
    else if (isOverrideColumn && !child[column.name]) display = html`<span class="field-display-empty">${followsParentLabel}</span>`;
    else if (column.type === FIELD_TYPE.RATING && !isCellReadOnly) {
      display = html`<${RatingStars} value=${ratingToNumber(child[column.name])} onSelect=${(rating) => commit(child, column, getInputOnlyPatch(column, child, rating))} />`;
    } else display = html`<${FieldDisplay} field=${column} record=${child} />`;
    return html`<td
      key=${column.id}
      class="table-cell"
      data-field-name=${column.name}
      data-readonly=${isCellReadOnly ? 'true' : undefined}
      data-editing=${isEditing ? 'true' : undefined}
      data-required=${isPendingReason ? 'true' : undefined}
      onClick=${(event) => {
        if (isCellReadOnly || isEditing || /** @type {HTMLElement} */ (event.target).closest('a, .rating-star')) return;
        if (INPUT_ONLY_FIELD_TYPES.has(column.type)) {
          if (column.type === FIELD_TYPE.BOOLEAN) commit(child, column, getInputOnlyPatch(column, child));
          return;
        }
        setEditingCell({ childId: child.id, fieldId: column.id });
      }}
    >
      <div class="table-cell-content">${display}</div>
      ${isEditing
        ? html`<${FieldInput}
            field=${column}
            record=${child}
            emptyOptionLabel=${isOverrideColumn ? followsParentLabel : null}
            createPriorityFieldNames=${column.id === identifyingField?.id ? lookupColumns.map((lookup) => lookup.field.name) : undefined}
            onCommit=${(patch) => {
              setEditingCell(null);
              commit(child, column, patch);
            }}
            onCancel=${() => {
              setEditingCell(null);
              if (isLostReasonColumn && pendingLostChildId === child.id) setPendingLostChildId(null);
            }}
          />`
        : null}
    </td>`;
  };

  /**
   * Célula de um campo do registro identificado (ex.: nascimento do aluno); edita a própria pessoa.
   * @param {any} child
   * @param {import('../../lib/related-records.js').LookupColumn} lookup
   */
  const renderLookupCell = (child, lookup) => {
    const related = lookupRecords.get(child[lookupIdFieldName]);
    const isCellReadOnly = isReadOnly || !related || Boolean(child.deletedAt) || isFieldReadOnly(lookup.field) || !isFieldEditableInline(lookup.field);
    const isEditing = !isCellReadOnly && editingCell?.childId === child.id && editingCell.fieldId === lookup.id;
    return html`<td
      key=${lookup.id}
      class="table-cell"
      data-field-name=${lookup.field.name}
      data-readonly=${isCellReadOnly ? 'true' : undefined}
      data-editing=${isEditing ? 'true' : undefined}
      onClick=${(event) => {
        if (isCellReadOnly || isEditing || /** @type {HTMLElement} */ (event.target).closest('a')) return;
        setEditingCell({ childId: child.id, fieldId: lookup.id });
      }}
    >
      <div class="table-cell-content">${related ? html`<${FieldDisplay} field=${lookup.field} record=${related} />` : null}</div>
      ${isEditing && identifiedObject
        ? html`<${FieldInput}
            field=${lookup.field}
            record=${related}
            onCommit=${(patch) => {
              setEditingCell(null);
              if (!patch) return;
              setLookupRecords((current) => new Map(current).set(related.id, { ...related, ...patch.localPatch }));
              updateRecordOptimistically(identifiedObject, related.id, patch.apiPatch, patch.localPatch)
                .then(() => setReloadCounter((current) => current + 1))
                .catch(() => {});
            }}
            onCancel=${() => setEditingCell(null)}
          />`
        : null}
    </td>`;
  };

  return html`<section class="related-records-section" data-field-name=${field.name}>
    <div class="related-records-header">
      <${Icon} name=${getFieldIcon(field)} size=${16} />
      <span class="related-records-title">${translateMetadataLabel(field.label)}</span>
      <span class="related-records-count">${isLoading ? '' : children.length}</span>
    </div>
    <div class="related-records-table-frame">
      <table class="record-table related-records-table">
        <thead>
          <tr>
            ${tableColumns.map((entry) => {
              const column = entry.kind === 'own' ? entry.column : entry.lookup.field;
              const key = entry.kind === 'own' ? entry.column.id : entry.lookup.id;
              const label = entry.kind === 'lookup' && entry.lookup.shortLabelKey ? t(entry.lookup.shortLabelKey) : translateMetadataLabel(column.label);
              return html`<th key=${key} class="table-header-cell" style=${{ width: getColumnWidth({ id: key }) }}>
                <div class="table-header-cell-content" title=${label}>
                  <${Icon} name=${getFieldIcon(column)} size=${16} />
                  <span class="table-header-cell-label">${label}</span>
                </div>
              </th>`;
            })}
            <th class="table-header-cell table-filler-cell" style=${{ width: `${ACTIONS_COLUMN_WIDTH}px` }}></th>
          </tr>
        </thead>
        <tbody>
          ${isLoading
            ? [0, 1].map((rowIndex) => html`<tr key=${`skeleton-${rowIndex}`} class="table-row">
                ${tableColumns.map((entry, index) => html`<td key=${index} class="table-cell"><div class="table-cell-content"><${Skeleton} width="70%" height=${12} /></div></td>`)}
                <td class="table-cell table-filler-cell"></td>
              </tr>`)
            : children.map((child) => html`<tr key=${child.id} class="table-row" data-record-id=${child.id}>
                ${tableColumns.map((entry) => (entry.kind === 'own' ? renderCell(child, entry.column) : renderLookupCell(child, entry.lookup)))}
                <td class="table-cell table-filler-cell related-records-actions-cell" data-readonly="true">
                  <${RowActionsMenu}
                    objectMetadata=${objectMetadata}
                    record=${record}
                    child=${child}
                    targetObject=${targetObject}
                    isReadOnly=${isReadOnly}
                    onOpen=${() => openRecord({ objectNameSingular: targetObject.nameSingular, recordId: child.id })}
                    onRemove=${() => removeChild(child)}
                  />
                </td>
              </tr>`)}
          ${!isLoading && children.length === 0
            ? html`<tr class="table-row"><td class="table-cell related-records-empty" colspan=${tableColumns.length + 1} data-readonly="true">
                <div class="table-cell-content">${t('relatedRecords.emptyTitle')}</div>
              </td></tr>`
            : null}
        </tbody>
      </table>
    </div>
    ${isReadOnly
      ? null
      : html`<div class="related-records-footer">
          <${LightButton}
            icon="IconPlus"
            accent="tertiary"
            title=${translateForObject('add', targetObject.nameSingular, { object: objectLabel })}
            disabled=${isCreating}
            onClick=${addChild}
          />
        </div>`}
  </section>`;
}

